// src/components/support/SupportContactForm.js
// "Still need help?" in-app contact form shown at the bottom of Help Center pages.
// Pre-fills name/email from Firebase Auth. Calls submitSupportRequest Cloud Function.

import React, { useState, useEffect } from 'react';
import { Mail } from 'lucide-react';
import { auth } from '../../config/firebase-config';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { trackEvent } from '../../utils/analytics';

const CATEGORIES = [
  'Account & login issues',
  'Booking & scheduling',
  'Payments & billing',
  'Site Editor & website',
  'Technical issue / bug',
  'Feature request',
  'Other',
];

const SupportContactForm = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [category, setCategory] = useState('');
  const [message, setMessage] = useState('');
  const [status, setStatus] = useState('idle'); // idle | sending | success | error
  const [errorMsg, setErrorMsg] = useState('');
  const [buttonHover, setButtonHover] = useState(false);

  const userRole = localStorage.getItem('userRole') || 'practitioner';
  const isPractitioner = userRole === 'practitioner' || userRole === 'admin';

  // Pre-fill from auth
  useEffect(() => {
    const user = auth?.currentUser;
    if (user) {
      setName(user.displayName || localStorage.getItem('userName') || '');
      setEmail(user.email || localStorage.getItem('userEmail') || '');
    } else {
      setName(localStorage.getItem('userName') || '');
      setEmail(localStorage.getItem('userEmail') || '');
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim() || !message.trim()) return;

    setStatus('sending');
    setErrorMsg('');

    try {
      const functions = getFunctions(undefined, 'us-west1');
      const submitRequest = httpsCallable(functions, 'submitSupportRequest');
      await submitRequest({
        name: name.trim(),
        email: email.trim(),
        category: category || 'General',
        message: message.trim(),
        source: 'help_center',
      });
      setStatus('success');
      trackEvent('contact_submitted', { source: 'help_center' });
      setMessage('');
      setCategory('');
    } catch (err) {
      console.error('Support request failed:', err);
      setErrorMsg(err.message || 'Something went wrong.');
      setStatus('error');
    }
  };

  // Filter out practitioner-only categories for seekers
  const visibleCategories = isPractitioner
    ? CATEGORIES
    : CATEGORIES.filter(c => c !== 'Site Editor & website');

  if (status === 'success') {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-7">
        <div className="text-center py-4">
          <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-3">
            <Mail className="w-6 h-6 text-green-600" />
          </div>
          <h3 className="font-semibold text-gray-900 mb-2">Message sent!</h3>
          <p className="text-sm text-gray-600 max-w-md mx-auto">
            We've sent you a confirmation email. We typically respond within 1-2 business days.
          </p>
          <span
            role="button"
            tabIndex={0}
            onClick={() => setStatus('idle')}
            onKeyDown={(e) => { if (e.key === 'Enter') setStatus('idle'); }}
            className="inline-block mt-4 text-sm text-purple-600 hover:text-purple-700 font-medium cursor-pointer"
            style={{ userSelect: 'none' }}
          >
            Send another message
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-7">
      <div className="flex items-start gap-4 mb-6">
        <div className="w-11 h-11 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
          <Mail className="w-5 h-5 text-gray-500" />
        </div>
        <div>
          <h3 className="font-semibold text-gray-900 mb-1">Still need help?</h3>
          <p className="text-sm text-gray-600">
            We typically respond within 1-2 business days.
          </p>
        </div>
      </div>

      {status === 'error' && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-700">
            {errorMsg || 'Something went wrong. Please try again or email us directly at '}
            {!errorMsg && (
              <a href="mailto:support@divineframeworks.com" className="font-medium underline">
                support@divineframeworks.com
              </a>
            )}
          </p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              disabled={status === 'sending'}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent disabled:opacity-50"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={status === 'sending'}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent disabled:opacity-50"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            disabled={status === 'sending'}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent disabled:opacity-50 bg-white"
          >
            <option value="">Select a category...</option>
            {visibleCategories.map(cat => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Message</label>
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value.slice(0, 2000))}
            required
            disabled={status === 'sending'}
            rows={4}
            placeholder="Describe what you need help with..."
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none disabled:opacity-50"
          />
          <div className="text-right mt-1">
            <span className="text-xs text-gray-400">{message.length}/2000</span>
          </div>
        </div>

        <div className="flex justify-end">
          <button
            type="submit"
            disabled={status === 'sending' || !name.trim() || !email.trim() || !message.trim()}
            className="px-6 py-2.5 text-sm font-semibold text-white rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ backgroundColor: buttonHover ? '#a8882f' : '#bb9749' }}
            onMouseEnter={() => setButtonHover(true)}
            onMouseLeave={() => setButtonHover(false)}
          >
            {status === 'sending' ? (
              <span className="flex items-center gap-2">
                <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Sending...
              </span>
            ) : (
              'Send message'
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default SupportContactForm;
